import { useEffect, useMemo, useState } from 'react'
import { enregistrerForfaits, enregistrerTarifs } from '../../api/operations'
import { brouillonForfaits, forfaitsModifies, lireBrouillonForfaits, type BrouillonForfait } from '../../api/forfaits'
import { apercuBareme, brouillonDepuis, lireBrouillon, nombreFR, type Brouillon } from '../../api/reglagesTarifs'
import { calculerPrixOpere, LIBELLE_VEHICULE, VEHICULES, type TypeVehicule } from '../../api/prixOpere'
import type { DonneesOps } from '../../api/types'
import { BilanPrix } from '../BilanPrix'
import { useDroits } from '../Contexte'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { enregistrerPaysOrganisation, lirePaysOrganisation, optionsPays } from '../../api/paysOrganisation'

/**
 * Réglages — la grille tarifaire de l'offre opérée et les frais mobile money (propriétaire seulement).
 *
 * Le prix de TOUTE course opérée géolocalisée vient de cette grille : la base la lit et verrouille le
 * prix (migration 0030). Modifier une valeur ici change le prix des courses créées ensuite ; les
 * courses déjà créées gardent le leur. Une calculatrice, alimentée par le BROUILLON (pas encore
 * enregistré), montre l'effet avant d'enregistrer.
 */
export function EcranReglages({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const peutEcrire = droits.peut('tarifs') && !droits.apercu
  const tarifs = donnees.tarifs
  const [b, setB] = useState<Brouillon | null>(() => (tarifs ? brouillonDepuis(tarifs) : null))
  const [enCours, setEnCours] = useState(false)
  const [modifie, setModifie] = useState(false)

  /* Les données reviennent du serveur (enregistrement, autre onglet) : le brouillon les suit tant
     qu'on n'a rien modifié à la main. */
  useEffect(() => {
    if (tarifs && !modifie) setB(brouillonDepuis(tarifs))
  }, [tarifs, modifie])

  const lecture = useMemo(() => (b ? lireBrouillon(b) : null), [b])

  // Forfaits PME (0031) : même mécanique — le brouillon suit le serveur tant qu'on n'a rien modifié à la main.
  const [bf, setBf] = useState<BrouillonForfait[]>(() => brouillonForfaits(donnees.forfaits))
  const [modifieF, setModifieF] = useState(false)
  const [enCoursF, setEnCoursF] = useState(false)
  useEffect(() => {
    if (!modifieF) setBf(brouillonForfaits(donnees.forfaits))
  }, [donnees.forfaits, modifieF])
  const lectureF = useMemo(() => lireBrouillonForfaits(bf), [bf])
  const majF = (code: string, patch: Partial<BrouillonForfait>) => {
    setModifieF(true)
    setBf((l) => l.map((f) => (f.code === code ? { ...f, ...patch } : f)))
  }
  async function enregistrerLesForfaits() {
    if (!lectureF.ok || enCoursF) return
    const aEcrire = forfaitsModifies(bf, donnees.forfaits).map((m) => lectureF.forfaits.find((f) => f.code === m.code)).filter((f): f is NonNullable<typeof f> => !!f)
    if (aEcrire.length === 0) {
      setModifieF(false)
      return
    }
    setEnCoursF(true)
    try {
      await enregistrerForfaits(orgId, aEcrire)
      setModifieF(false)
      surNotifier('Forfaits enregistrés — les abonnements existants gardent leur prix et leur quota')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCoursF(false)
    }
  }

  // Calculatrice
  const [dist, setDist] = useState('8')
  const [veh, setVeh] = useState<TypeVehicule>('moto')
  const [option, setOption] = useState(false)
  const [horsCreneau, setHorsCreneau] = useState(false)
  const distance = nombreFR(dist)
  const apercu = useMemo(() => {
    if (!lecture || !lecture.ok || !(distance >= 0)) return null
    /* La tension dépend des courses et des livreurs du moment : la calculatrice montre le prix hors majoration. */
    return calculerPrixOpere({ ...lecture.grille, tension_active: false }, { distance_km: distance, vehicule: veh, option_longue_distance: option, hors_creneau: horsCreneau })
  }, [lecture, distance, veh, option, horsCreneau])

  // Aperçu du barème (0043) : le prix aux distances de référence, brouillon et grille enregistrée côte à côte.
  const [vehApercu, setVehApercu] = useState<TypeVehicule>('moto')
  const lignesApercu = useMemo(
    () => (lecture && lecture.ok ? apercuBareme(lecture.grille, vehApercu, tarifs) : null),
    [lecture, vehApercu, tarifs],
  )

  if (!tarifs || !b) {
    return (
      <>
        <div className="bandeau alerte" role="alert">
          <strong>La grille tarifaire est introuvable.</strong> Elle est créée par la migration 0030 pour l’organisation opératrice : tant qu’elle n’est pas
          appliquée à la base, la prise d’appel retombe sur la saisie manuelle du prix.
        </div>
        <div className="grille-reglages" style={{ marginTop: 16 }}>
          <CartePaysNumeros orgId={orgId} peutEcrire={droits.peut('parametres_org') && !droits.apercu} surNotifier={surNotifier} />
        </div>
      </>
    )
  }

  const maj = (patch: Partial<Brouillon>) => {
    setModifie(true)
    setB({ ...b, ...patch })
  }
  const majTranche = (i: number, patch: Partial<Brouillon['tranches'][number]>) =>
    maj({ tranches: b.tranches.map((t, k) => (k === i ? { ...t, ...patch } : t)) })
  const majMm = (patch: Partial<Brouillon['mm']>) => maj({ mm: { ...b.mm, ...patch } })
  const majTension = (patch: Partial<Brouillon['tension']>) => maj({ tension: { ...b.tension, ...patch } })

  async function enregistrer() {
    if (!lecture || !lecture.ok || enCours) return
    setEnCours(true)
    try {
      await enregistrerTarifs(orgId, lecture.tarifs)
      setModifie(false)
      surNotifier('Grille tarifaire enregistrée — appliquée aux prochaines courses')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  const champ = (id: string, etiquette: string, valeur: string, surChange: (v: string) => void, aide?: string) => (
    <div className="field">
      <label htmlFor={id}>{etiquette}</label>
      <input id={id} inputMode="decimal" autoComplete="off" value={valeur} disabled={!peutEcrire} onChange={(e) => surChange(e.target.value)} />
      {aide && <div className="t-muted" style={{ fontSize: 11.5, marginTop: 4 }}>{aide}</div>}
    </div>
  )

  return (
    <>
      {tarifs.grille_provisoire && (
        <div className="bandeau alerte" role="status">
          <strong>Valeurs provisoires.</strong> Ces chiffres sont des ordres de grandeur posés par défaut : vérifiez-les puis enregistrez pour les valider.
          Dès l’enregistrement, cette mention disparaît.
        </div>
      )}
      {!peutEcrire && (
        <div className="bandeau">
          {droits.apercu ? 'Aperçu de développement : aucune écriture possible.' : `Lecture seule. ${droits.refus}`}
        </div>
      )}

      <div className="grille-reglages">
        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="pin" classe="h-ic" /> Barème kilométrique
              </h3>
              <div className="ch-sub">Barème PROGRESSIF : chaque ligne donne le prix à une distance ; entre deux points, le prix est interpolé (prix de base, avant coefficient du véhicule)</div>
            </div>
          </div>
          <div className="card-body">
            <div className="form form-grid" style={{ marginBottom: 10 }}>
              {champ('prix-min', 'Prix minimum — prix à 0 km (F)', b.minimum, (x) => maj({ minimum: x }), 'Premier point du barème et plancher : aucune course moto ne coûte moins (avant coefficient du véhicule).')}
            </div>
            <table className="table-saisie">
              <thead>
                <tr>
                  <th>Distance (km)</th>
                  <th>Prix à cette distance (F)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {b.tranches.map((t, i) => (
                  <tr key={i}>
                    <td>
                      <input aria-label={`Tranche ${i + 1} : distance`} inputMode="decimal" value={t.jusqu} disabled={!peutEcrire} onChange={(e) => majTranche(i, { jusqu: e.target.value })} />
                    </td>
                    <td>
                      <input aria-label={`Prix à ${t.jusqu || '…'} km`} inputMode="numeric" value={t.prix} disabled={!peutEcrire} onChange={(e) => majTranche(i, { prix: e.target.value })} />
                      <div className="t-muted" style={{ fontSize: 11 }}>prix à {t.jusqu || '…'} km</div>
                    </td>
                    <td style={{ width: 40 }}>
                      {peutEcrire && b.tranches.length > 1 && (
                        <button className="rm" type="button" title="Retirer la tranche" onClick={() => maj({ tranches: b.tranches.filter((_, k) => k !== i) })}>
                          <Icone nom="x" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {peutEcrire && b.tranches.length < 10 && (
              <button className="btn btn-ghost btn-sm" type="button" style={{ marginTop: 8 }} onClick={() => maj({ tranches: [...b.tranches, { jusqu: '', prix: '' }] })}>
                <Icone nom="plus" /> Ajouter une tranche
              </button>
            )}
            <div className="t-muted" style={{ fontSize: 12, marginTop: 10 }}>
              Distances strictement croissantes ; un prix ne peut pas baisser quand la distance augmente. Exemple : 0 km à 1 000 F et 3 km à
              1 500 F → 1 km coûte 1 167 F, arrondi à 1 150 F. Au-delà du dernier point, c’est son prix qui s’applique, puis la longue distance ci-dessous.
            </div>
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="package" classe="h-ic" /> Coefficient par véhicule
              </h3>
              <div className="ch-sub">Multiplie le prix de base (1 = moto, le barème de référence)</div>
            </div>
          </div>
          <div className="card-body">
            <div className="grille-coefs">
              {VEHICULES.map((v) => champ(`coef-${v}`, LIBELLE_VEHICULE[v], b.coefs[v], (x) => maj({ coefs: { ...b.coefs, [v]: x } })))}
            </div>
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="arrow" classe="h-ic" /> Longue distance
              </h3>
              <div className="ch-sub">Chaque km entamé au-delà du seuil est facturé à part (sans coefficient)</div>
            </div>
          </div>
          <div className="card-body">
            <div className="form form-grid">
              {champ('seuil', 'Seuil (km)', b.seuil, (x) => maj({ seuil: x }))}
              {champ('kmsup', 'Prix du km supplémentaire (F)', b.kmSup, (x) => maj({ kmSup: x }))}
              {champ('kmsupopt', 'Km supplémentaire avec l’option (F)', b.kmSupOption, (x) => maj({ kmSupOption: x }), 'Tarif réduit des PME qui ont l’option mensuelle.')}
              {champ('optmens', 'Option longue distance, par mois (F)', b.optionMensuel, (x) => maj({ optionMensuel: x }), 'Abonnement mensuel facturé en plus du forfait PME ; son prix est figé le jour où l’option est activée sur la fiche du client.')}
            </div>
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="clock" classe="h-ic" /> Hors créneau et arrondi
              </h3>
              <div className="ch-sub">Le hors-créneau est jugé par la base, à l’heure de Bamako</div>
            </div>
          </div>
          <div className="card-body">
            <div className="form form-grid">
              {champ('supp', 'Supplément hors créneau par défaut (F)', b.supplement, (x) => maj({ supplement: x }), 'La fiche d’un client PME prime quand elle fixe son propre supplément.')}
              {champ('arr', 'Arrondi (F)', b.arrondi, (x) => maj({ arrondi: x }), 'Prix de la course arrondi au multiple le plus proche. 1 = pas d’arrondi.')}
            </div>
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="spark" classe="h-ic" /> Majoration « forte demande »
              </h3>
              <div className="ch-sub">Quand les courses en attente dépassent les livreurs disponibles autour du point d’enlèvement</div>
            </div>
          </div>
          <div className="card-body">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, marginBottom: 10 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={b.tension.active} disabled={!peutEcrire} onChange={(e) => majTension({ active: e.target.checked })} />
              Activer la majoration (désactivée par défaut)
            </label>
            <div className="form form-grid">
              {champ('t-rayon', 'Rayon autour de l’enlèvement (km)', b.tension.rayon, (x) => majTension({ rayon: x }), 'À vol d’oiseau. Livreurs comptés : disponibles, position de moins de 5 min.')}
              {champ('t-seuil', 'Seuil (courses par livreur)', b.tension.seuil, (x) => majTension({ seuil: x }), 'Ratio = courses en attente (celle-ci comprise) ÷ livreurs disponibles (au moins 1).')}
              {champ('t-pente', 'Pente (% par point de ratio)', b.tension.pente, (x) => majTension({ pente: x }))}
              {champ('t-plafond', 'Plafond (%)', b.tension.plafond, (x) => majTension({ plafond: x }))}
              {champ('t-part', 'Part reversée au livreur (%)', b.tension.partLivreur, (x) => majTension({ partLivreur: x }), 'Figée par course ; totalisée chaque mois pour la paie des livreurs.')}
            </div>
            {lecture?.ok && (
              <div className="t-muted" style={{ fontSize: 12, marginTop: 10 }}>
                Exemple (moto, 2 km, 3 courses pour 1 livreur) :{' '}
                {(() => {
                  const r = calculerPrixOpere({ ...lecture.grille, tension_active: true }, { distance_km: 2, tension: { demande: 3, offre: 1 } })
                  return `${fcfa(r.prix_course)} + ${fcfa(r.majoration_tension_fcfa ?? 0)} (forte demande, +${String(r.tension?.pct ?? 0).replace('.', ',')} %), dont ${fcfa(r.part_livreur_tension_fcfa ?? 0)} pour le livreur.`
                })()}{' '}
                Jamais appliquée à un client couvert par un forfait PME ; le supplément hors créneau n’est pas majoré.
              </div>
            )}
            {!tarifs.bareme_progressif_en_base && (
              <div className="bandeau alerte" role="status" style={{ marginTop: 10 }}>
                La base n’a pas encore la migration 0043 : le prix minimum et la majoration ne peuvent pas être enregistrés.
              </div>
            )}
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="wallet" classe="h-ic" /> Frais mobile money
              </h3>
              <div className="ch-sub">En % du montant, entre 0 et 10 % — à renseigner quand les accords Wave / Orange Money sont signés (0 = non facturé)</div>
            </div>
          </div>
          <div className="card-body">
            <div className="form form-grid">
              {champ('mm-ew', 'Encaissement Wave (%)', b.mm.encaissementWave, (x) => majMm({ encaissementWave: x }))}
              {champ('mm-eo', 'Encaissement Orange Money (%)', b.mm.encaissementOm, (x) => majMm({ encaissementOm: x }))}
              {champ('mm-dw', 'Décaissement Wave (%)', b.mm.decaissementWave, (x) => majMm({ decaissementWave: x }))}
              {champ('mm-do', 'Décaissement Orange Money (%)', b.mm.decaissementOm, (x) => majMm({ decaissementOm: x }))}
            </div>
            {lecture?.ok && (
              <div className="t-muted" style={{ fontSize: 12, marginTop: 10 }}>
                Sur un encaissement de 10 000 F : Wave {fcfa(Math.round(10000 * lecture.tarifs.frais_mm_encaissement_wave))} · Orange Money{' '}
                {fcfa(Math.round(10000 * lecture.tarifs.frais_mm_encaissement_om))}.
              </div>
            )}
          </div>
        </div>

        <CartePaysNumeros orgId={orgId} peutEcrire={droits.peut('parametres_org') && !droits.apercu} surNotifier={surNotifier} />

        <div className="card" data-anim style={{ gridColumn: '1 / -1' }}>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="receipt" classe="h-ic" /> Forfaits PME
              </h3>
              <div className="ch-sub">Prix mensuel TTC et courses incluses — modifier un forfait ne touche pas les abonnements existants (prix et quota figés)</div>
            </div>
          </div>
          <div className="card-body">
            {!donnees.synchro.abonnements ? (
              <div className="t-muted">Forfaits illisibles : la migration 0031 n’est pas appliquée à la base.</div>
            ) : (
              <>
                <table className="table-saisie">
                  <thead>
                    <tr>
                      <th>Forfait</th>
                      <th>Prix mensuel (F)</th>
                      <th>Courses / mois</th>
                      <th>Actif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bf.map((f) => (
                      <tr key={f.code}>
                        <td>
                          <input aria-label={`Libellé du forfait ${f.code}`} value={f.libelle} disabled={!peutEcrire} onChange={(e) => majF(f.code, { libelle: e.target.value })} />
                          <div className="t-muted" style={{ fontSize: 11 }}>{f.code}</div>
                        </td>
                        <td>
                          <input aria-label={`Prix du forfait ${f.code}`} inputMode="numeric" value={f.prix} placeholder="sur devis" disabled={!peutEcrire} onChange={(e) => majF(f.code, { prix: e.target.value })} />
                        </td>
                        <td>
                          <input aria-label={`Quota du forfait ${f.code}`} inputMode="numeric" value={f.quota} placeholder="illimité" disabled={!peutEcrire} onChange={(e) => majF(f.code, { quota: e.target.value })} />
                        </td>
                        <td style={{ width: 54, textAlign: 'center' }}>
                          <input type="checkbox" style={{ width: 'auto' }} aria-label={`Forfait ${f.code} actif`} checked={f.actif} disabled={!peutEcrire} onChange={(e) => majF(f.code, { actif: e.target.checked })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="t-muted" style={{ fontSize: 12, marginTop: 10 }}>
                  Prix vide = « sur devis » : chaque client porte alors son prix négocié. Quota vide = courses illimitées (jamais de dépassement). Au-delà du quota,
                  chaque course est facturée à son prix de grille. Un forfait désactivé n’est plus proposé pour une NOUVELLE souscription.
                </div>
                {lectureF && !lectureF.ok && (
                  <div className="bandeau erreur" role="alert" style={{ marginTop: 10 }}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {lectureF.problemes.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {peutEcrire && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
                    <button className="btn btn-gold btn-sm" onClick={() => void enregistrerLesForfaits()} disabled={!modifieF || !lectureF.ok || enCoursF}>
                      <Icone nom="check" /> {enCoursF ? 'Enregistrement…' : 'Enregistrer les forfaits'}
                    </button>
                    {modifieF && (
                      <button className="btn btn-ghost btn-sm" onClick={() => { setModifieF(false); setBf(brouillonForfaits(donnees.forfaits)) }}>
                        Annuler les modifications
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="receipt" classe="h-ic" /> Aperçu du barème
              </h3>
              <div className="ch-sub">Prix de la course avec le brouillon, avant d’enregistrer (sans supplément ni majoration)</div>
            </div>
          </div>
          <div className="card-body">
            <div className="field" style={{ maxWidth: 240, marginBottom: 10 }}>
              <label>Véhicule</label>
              <ListeDeroulante valeur={vehApercu} pleineLargeur options={VEHICULES.map((v) => ({ valeur: v, libelle: LIBELLE_VEHICULE[v] }))} surChangement={(v) => setVehApercu(v as TypeVehicule)} />
            </div>
            {lignesApercu ? (
              <table className="table-saisie">
                <thead>
                  <tr>
                    <th>Distance</th>
                    <th style={{ textAlign: 'right' }}>Brouillon</th>
                    <th style={{ textAlign: 'right' }}>Grille enregistrée</th>
                  </tr>
                </thead>
                <tbody>
                  {lignesApercu.map((l) => (
                    <tr key={l.km}>
                      <td>{String(l.km).replace('.', ',')} km</td>
                      <td style={{ textAlign: 'right', fontWeight: l.avant !== null && l.prix !== l.avant ? 700 : 400 }}>{fcfa(l.prix)}</td>
                      <td style={{ textAlign: 'right' }} className="t-muted">{l.avant === null ? '—' : fcfa(l.avant)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="t-muted">Corrigez le brouillon (voir les messages en bas de page) pour voir l’aperçu.</div>
            )}
          </div>
        </div>

        <div className="card" data-anim>
          <div className="card-head">
            <div>
              <h3>
                <Icone nom="spark" classe="h-ic" /> Calculatrice
              </h3>
              <div className="ch-sub">Teste le brouillon ci-contre — rien n’est enregistré</div>
            </div>
          </div>
          <div className="card-body">
            <div className="form form-grid">
              {champ('calc-km', 'Distance de route (km)', dist, setDist)}
              <div className="field">
                <label>Véhicule</label>
                <ListeDeroulante valeur={veh} pleineLargeur options={VEHICULES.map((v) => ({ valeur: v, libelle: LIBELLE_VEHICULE[v] }))} surChangement={(v) => setVeh(v as TypeVehicule)} />
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={option} onChange={(e) => setOption(e.target.checked)} /> Client avec l’option longue distance
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={horsCreneau} onChange={(e) => setHorsCreneau(e.target.checked)} /> Hors créneau
              </label>
            </div>
            <div style={{ marginTop: 12 }}>
              {apercu ? (
                <BilanPrix detail={apercu} titreTotal="Prix de la course" />
              ) : (
                <div className="t-muted">Complétez les champs (nombres valides, barème croissant) pour voir le prix.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {lecture && !lecture.ok && (
        <div className="bandeau erreur" role="alert" style={{ marginTop: 16 }}>
          <strong>À corriger avant d’enregistrer :</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {lecture.problemes.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button className="btn btn-gold" style={{ width: 'auto' }} onClick={() => void enregistrer()} disabled={!peutEcrire || !lecture?.ok || enCours}>
          <Icone nom="check" /> {enCours ? 'Enregistrement…' : 'Enregistrer la grille'}
        </button>
        {tarifs.updated_at && !tarifs.grille_provisoire && <span className="t-muted">Dernière modification : {dateCourte(tarifs.updated_at)}</span>}
        {modifie && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ width: 'auto' }}
            onClick={() => {
              setModifie(false)
              setB(brouillonDepuis(tarifs))
            }}
          >
            Annuler les modifications
          </button>
        )}
      </div>
    </>
  )
}

/**
 * Pays proposé d'office dans les champs « numéro WhatsApp » (`organizations.country`, migrations 0041-0042) — propriétaire
 * seulement (policy `org_owner_maj`). C'est le pays de l'organisation opératrice : celui de l'écran de connexion de TOUTES
 * les apps et consoles, avant qu'on sache qui se connecte. Chaque personne peut en choisir un autre : son choix est
 * mémorisé sur son appareil.
 */
function CartePaysNumeros({ orgId, peutEcrire, surNotifier }: { orgId: string; peutEcrire: boolean; surNotifier: (m: string) => void }) {
  /* undefined = lecture en cours ; null = migration 0041 pas encore appliquée. */
  const [pays, setPays] = useState<string | null | undefined>(undefined)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    let vivant = true
    void lirePaysOrganisation(orgId)
      .then((p) => vivant && setPays(p))
      .catch(() => vivant && setPays(null))
    return () => {
      vivant = false
    }
  }, [orgId])

  async function choisir(iso: string) {
    setEnCours(true)
    try {
      await enregistrerPaysOrganisation(orgId, iso)
      setPays(iso)
      surNotifier('Pays par défaut enregistré')
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="card" data-anim>
      <div className="card-head">
        <div>
          <h3>
            <Icone nom="pin" classe="h-ic" /> Pays par défaut des numéros
          </h3>
          <div className="ch-sub">Connexion par numéro WhatsApp : pays proposé d’office dans les champs « numéro »</div>
        </div>
      </div>
      <div className="card-body">
        {pays === null ? (
          <div className="t-muted">Ce réglage apparaîtra une fois la migration 0041 appliquée à la base.</div>
        ) : (
          <div className="field">
            <label>Pays</label>
            <ListeDeroulante
              valeur={pays ?? ''}
              pleineLargeur
              options={optionsPays()}
              desactive={pays === undefined || enCours || !peutEcrire}
              surChangement={(v) => void choisir(v)}
            />
          </div>
        )}
        <div className="t-muted" style={{ fontSize: 12, marginTop: 10 }}>
          {peutEcrire
            ? 'Chaque personne peut en choisir un autre : son choix est mémorisé sur son appareil. Les numéros restent toujours enregistrés avec leur indicatif.'
            : 'Seul le propriétaire modifie ce réglage.'}
        </div>
      </div>
    </div>
  )
}
